import * as yup from "yup";
import { QuestionLogics, Questions, Option } from "Models/checklist";
import {
  MultipleLogicOption,
  NumberLogicOption,
  SingleLogicOption,
  TextLogicOption,
} from "Util/enums";

export enum QuestionTypes {
  SINGLE = "Radio",
  MULTIPLE = "Checkbox",
  TEXT = "Text",
  NUMBER = "Integer",
  DATE = "Date",
  SIGNATURE = "Signature",
  PHOTO = "Photo",
}

enum LegacyQuestionTypes {
  CHECKBOX_TEXT = "Checkbox_Text",
  RADIO_TEXT = "Radio_Text",
  DECIMAL = "Decimal",
  TEXT_AREA = "Textarea",
  PARAGRAPH = "Paragraph",
}

export const getLogicConditionInitialValue = ({
  questionType,
  isRequired,
}: {
  questionType: string;
  isRequired: boolean;
}) => {
  let logicNewCondition: string = TextLogicOption.IS;

  if (questionType === QuestionTypes.TEXT) {
    logicNewCondition = isRequired ? TextLogicOption.CONTAINS : TextLogicOption.IS;
  }

  if (questionType === QuestionTypes.NUMBER) {
    logicNewCondition = isRequired ? NumberLogicOption.IS_LESSER_THAN : NumberLogicOption.IS;
  }

  if (questionType === QuestionTypes.SINGLE) {
    logicNewCondition = SingleLogicOption.IS;
  }

  if (questionType === QuestionTypes.MULTIPLE) {
    logicNewCondition = MultipleLogicOption.SELECTED;
  }

  return logicNewCondition;
};

export const parseQuestionsWithLogic = (questionsArray: Questions[]) => {
  // Create a deep copy to avoid mutating the original array
  const questions: Questions[] = JSON.parse(JSON.stringify(questionsArray));

  const processQuestions = (questionsToProcess: Questions[], allQuestions: Questions[]) => {
    questionsToProcess.sort(
      (questionA, questionB) => (questionA.questionOrder || 0) - (questionB.questionOrder || 0)
    );

    questionsToProcess.forEach((question) => {
      if (question.type === LegacyQuestionTypes.DECIMAL) {
        question.type = QuestionTypes.NUMBER;
      }

      if (
        question.type === LegacyQuestionTypes.PARAGRAPH ||
        question.type === LegacyQuestionTypes.TEXT_AREA
      ) {
        question.type = QuestionTypes.TEXT;
      }

      if (question.type === LegacyQuestionTypes.CHECKBOX_TEXT) {
        question.type = QuestionTypes.MULTIPLE;
      }

      if (question.type === LegacyQuestionTypes.RADIO_TEXT) {
        question.type = QuestionTypes.SINGLE;
      }

      question.questionLogics?.forEach((logic) => {
        // Find questions that belong to this logic
        const relatedQuestions = allQuestions.filter(
          (question) => question.logicId && question.logicId === logic.id
        );

        relatedQuestions.sort(
          (questionA, questionB) => (questionA.questionOrder || 0) - (questionB.questionOrder || 0)
        );

        // Add the questions array to the logic object
        logic.questions = relatedQuestions;

        // Recursively process nested questions if they have questionLogics
        if (relatedQuestions.length > 0) {
          processQuestions(relatedQuestions, allQuestions);
        }
      });
    });
  };

  // Separate questions without logicId
  const rootQuestions = questions.filter((question) => !question.logicId);

  rootQuestions.sort(
    (questionA, questionB) => (questionA.questionOrder || 0) - (questionB.questionOrder || 0)
  );

  processQuestions(rootQuestions, questions);

  return rootQuestions;
};

export const updateLogicConditions = ({
  newCondition,
  questionType,
  logics,
}: {
  newCondition: string;
  questionType: any;
  logics?: QuestionLogics[];
}): QuestionLogics[] | undefined => {
  if (!logics) return logics;

  if (
    questionType === QuestionTypes.DATE ||
    questionType === QuestionTypes.PHOTO ||
    questionType === QuestionTypes.SIGNATURE
  ) {
    return [];
  }

  const newValue =
    (newCondition === TextLogicOption.IS ||
      newCondition === TextLogicOption.IS_NOT ||
      newCondition === NumberLogicOption.IS ||
      newCondition === NumberLogicOption.IS_NOT) &&
    (questionType === QuestionTypes.TEXT || questionType === QuestionTypes.NUMBER)
      ? "blank"
      : "";

  return logics.map((logic) => ({
    ...logic,
    logicCondition: newCondition,
    logicValue: newValue,
  }));
};

export const handleQuestionOptions = (typeValue: any, questionOptions?: Option[]): Option[] => {
  let newOptions = [...(questionOptions || [])];

  // if question type was changed to neither single or multiple, set all options valid to false
  if (typeValue !== QuestionTypes.SINGLE || typeValue !== QuestionTypes.MULTIPLE) {
    newOptions = questionOptions?.map((option) => ({ ...option, valid: false })) || [];
  }

  return newOptions;
};

export const getErrorByPath = ({
  path,
  errors,
}: {
  path: string;
  errors?: yup.ValidationError[];
}): string | undefined => {
  if (!errors) {
    return undefined;
  }

  const error = errors.find((err) => err.path === path);

  return error?.message;
};

export const findQuestionPath = ({
  currentPath = "questions",
  questions,
  questionId,
  questionTemporaryId,
}: {
  currentPath?: string;
  questions?: Questions[];
  questionId?: number;
  questionTemporaryId?: string;
}): string | null => {
  if (!questions) return null;

  for (let i = 0; i < questions.length; i++) {
    const question = questions[i];
    const questionPath = `${currentPath}[${i}]`;

    if (
      (questionId && question.id === questionId) ||
      (questionTemporaryId && question.temporaryId === questionTemporaryId)
    ) {
      return questionPath;
    }

    if (question.questionLogics) {
      for (let j = 0; j < question.questionLogics.length; j++) {
        const logic = question.questionLogics[j];

        const nestedPath = findQuestionPath({
          currentPath: `${questionPath}.questionLogics[${j}].questions`,
          questions: logic.questions,
          questionId,
          questionTemporaryId,
        });

        if (nestedPath) return nestedPath;
      }
    }
  }

  return null;
};

export const findLogicPath = ({
  currentPath = "questions",
  questions,
  logicId,
  logicTemporaryId,
}: {
  currentPath?: string;
  questions?: Questions[];
  logicId?: number;
  logicTemporaryId?: string;
}): string | null => {
  if (!questions) return null;

  for (let i = 0; i < questions.length; i++) {
    const question = questions[i];
    const questionPath = `${currentPath}[${i}]`;

    if (question.questionLogics) {
      for (let j = 0; j < question.questionLogics.length; j++) {
        const logic = question.questionLogics[j];
        const logicPath = `${questionPath}.questionLogics[${j}]`;

        if (
          (logicId && logic.id === logicId) ||
          (logicTemporaryId && logic.temporaryId === logicTemporaryId)
        ) {
          return logicPath;
        }

        const nestedPath = findLogicPath({
          currentPath: `${logicPath}.questions`,
          questions: logic.questions,
          logicId,
          logicTemporaryId,
        });

        if (nestedPath) return nestedPath;
      }
    }
  }

  return null;
};

export const useQuestionErrors = ({
  questionPath,
  errors,
}: {
  questionPath: string | null;
  errors?: yup.ValidationError[];
}) => {
  if (!questionPath) {
    return {
      titleError: undefined,
      typeError: undefined,
      optionsError: undefined,
      descriptionError: undefined,
    };
  }

  return {
    titleError: getErrorByPath({ path: `${questionPath}.title`, errors }),
    typeError: getErrorByPath({ path: `${questionPath}.type`, errors }),
    optionsError: getErrorByPath({ path: `${questionPath}.options`, errors }),
  };
};

export const useLogicErrors = ({
  logicPath,
  errors,
}: {
  logicPath: string | null;
  errors?: yup.ValidationError[];
}) => {
  if (!logicPath) {
    return {
      logicConditionError: undefined,
      logicValueError: undefined,
    };
  }

  return {
    logicConditionError: getErrorByPath({ path: `${logicPath}.logicCondition`, errors }),
    logicValueError: getErrorByPath({ path: `${logicPath}.logicValue`, errors }),
  };
};

export const questionTags = {
  order: {
    "@order.type": "Order Type",
    "@order.reference": "Reference",
    "@order.branchOrigin": "Branch Origin",
    "@order.branchDestination": "Branch Destination",
    "@order.status": "Status",
    "@order.consultant": "Consultant",
    "@order.code": "Code",
    "@order.manager": "Manager",
    "@order.leadSourceFrom": "Lead source from",
    "@order.mode": "Mode",
    "@order.pickupAccess": "Pickup Access",
    "@order.deliveryAccess": "Delivery Access",
    "@order.allowance": "Allowance",
    "@order.secondReference": "Second Reference",
  },
};

export const statementOfAspTags: Record<string, string> = {
  "@order.customer": "Customer Name",
  "@order.dateOfTask": "Task Date",
};

export const statementOfAspGoShareTags: Record<string, string> = {
  "@order.grade": "Grade",
  "@order.scac": "SCAC",
};
